import React from 'react'
import { css } from '@emotion/css'
import { useIntl } from 'react-intl'
import { CodeBlock } from '@contrast-security-inc/design-system-surfaces'
import { Tab, TabList } from '@contrast-security-inc/design-system-navigations'
import {
  stepAgentConfig,
  stepSelectContainerMethod,
  stepInteractContrastUI,
  getColumnsEnv,
  getEnvVarsData,
  stepAgentConfigPipeline,
  stepSelectDockerMethod,
  stepSelectOperatingSystemLegacyNoDetails,
  stepConfigureAppSettingsAzureNormalize
} from './common'
import {
  stepContainerRunInstaller,
  stepContainerAddContrastConfigurationToImage,
  stepContainerRunYourContainer,
  stepContainerInstallOperator,
  stepContainerConfigureOperator,
  stepContainerInjectWorkloads as stepContainerInjectWorkloadsDotnetCore,
  stepContainerClusterAgentAndAgent
} from './common-container'
import {
  stepKubernetesDownloadSetup,
  stepKubernetesInstallDeployHelm,
  stepKubernetesLabelDeployments as stepKubernetesLabelDeploymentsDotnetCore,
  stepSelectKubernetesOperator
} from './common-kubernetes'
import { StepContent } from '../agent-wizard-steps'
import { formatStyles } from '../../../../react-intl-helpers'
import { ImageIcon } from '../common/image-icon'
import {
  logo_azure,
  logo_docker,
  logo_dotnetcore,
  logo_manual,
  logo_pipeline
} from '../images'
import {
  IconBitbucket,
  IconGithub
} from '@contrast-security-inc/design-system-foundations'

const stepKubernetesLabelDeployments =
  stepKubernetesLabelDeploymentsDotnetCore('DOTNET_CORE')

const stepContainerInjectWorkloads =
  stepContainerInjectWorkloadsDotnetCore('DOTNET_CORE')

const commonFinalSteps = [stepInteractContrastUI]

const coreclrProfiler = '{8B2CE134-0948-48CA-A4B2-80DDAD9F5791}'

const stepInstallAgentBasic = {
  defaultIsOpen: true,
  titleMsg: 'title_installAgent',
  Content: {
    message: 'detail_downloadAndExtractAgent',
    values: {
      os: 'linux',
      code: `curl -L https://www.nuget.org/api/v2/package/Contrast.SensorsNetCore/ --output Contrast.SensorsNetCore.nupkg &&
unzip Contrast.SensorsNetCore.nupkg -d Contrast.SensorsNetCore &&
mv Contrast.SensorsNetCore/contentFiles/any/netstandard2.0/contrast ./`
    }
  }
}

const selectMethodDotNetCoreNormalizeAgentDownload = {
  titleMsg: 'title_applicationDeploymentMethod',
  defaultIsOpen: true,
  Content: {
    options: [
      {
        value: 'basic',
        Content: {
          message: 'title_installManually'
        },
        icon: <ImageIcon src={logo_manual} alt="manually" />
      },
      {
        value: 'container',
        Content: {
          message: 'title_installContainer'
        },
        icon: <ImageIcon src={logo_docker} alt="container" />
      },
      {
        value: 'pipeline',
        Content: {
          message: 'title_installPipeline'
        },
        icon: <ImageIcon src={logo_pipeline} alt="container" />
      }
    ]
  }
}

const selectWindowsMethodDotNetCoreNormalizeAgentDownload = {
  titleMsg: 'title_applicationDeploymentMethod',
  defaultIsOpen: true,
  Content: {
    options: [
      {
        value: 'installer',
        Content: {
          message: 'title_installWithContrastInstaller'
        },
        icon: <ImageIcon src={logo_dotnetcore} alt="installer" />
      },
      {
        value: 'basic',
        Content: {
          message: 'title_installManually'
        },
        icon: <ImageIcon src={logo_manual} alt="manually" />
      },
      {
        value: 'container',
        Content: {
          message: 'title_installContainer'
        },
        icon: <ImageIcon src={logo_docker} alt="container" />
      },
      {
        value: 'azure',
        Content: {
          message: 'title_installWithAzure'
        },
        icon: <ImageIcon src={logo_azure} alt="azure" />
      },
      {
        value: 'pipeline',
        Content: {
          message: 'title_installPipeline'
        },
        icon: <ImageIcon src={logo_pipeline} alt="container" />
      }
    ]
  }
}

const stepsDockerContainerNormalizeAgentDownload = [
  {
    titleMsg: 'title_buildImageWithAgent',
    defaultIsOpen: true,
    Content: () => {
      const [tab, setTab] = React.useState('x64')

      return (
        <>
          <StepContent
            step={{
              Content: {
                message: 'detail_addBuildImageWithAgent'
              }
            }}
          />
          <TabList
            className={css`
              margin-bottom: 16px;
            `}
            value={tab}
            onChange={(_, value) => setTab(value)}
          >
            <Tab label="x64" value="x64" />
            <Tab label="ARM64" value="arm64" />
          </TabList>
          <CodeBlock
            style={{ width: '100%', maxHeight: 'none' }}
            message={`From mcr.microsoft.com/dotnet/aspnet:6.0

# Hidden for brevity...

COPY --from=contrast/agent-dotnet-core:latest /contrast /contrast

# Required variables to load the agent.
ENV CORECLR_PROFILER_PATH 64=/contrast/runtimes/linux-${tab}/native/ContrastProfiler.so \\
      CORECLR_ENABLE_PROFILING=1
      CORECLR_PROFILER={8B2CE134-0948-48CA-A4B2-80DDAD9F5791}`}
          />
          <StepContent
            step={{
              Content: {
                message: 'detail_addBuildImage',
                values: {
                  code: 'docker build -t my-app-with-contrast .'
                }
              }
            }}
          />
        </>
      )
    }
  },
  stepAgentConfig,
  stepContainerAddContrastConfigurationToImage,
  stepContainerRunYourContainer,
  ...commonFinalSteps
]

// LINUX STEPS
const stepsLinuxBasicNormalizeAgentDownload = [
  stepInstallAgentBasic,
  stepAgentConfig,
  {
    titleMsg: 'title_setEnvVariables',
    Content: () => (
      <>
        <StepContent
          step={{
            Content: {
              message: 'title_dotNetCoresetEnvVariablesA',
              values: formatStyles.table({
                columns: getColumnsEnv(useIntl()),
                data: getEnvVarsData('linux')
              })
            }
          }}
        />
        <StepContent
          step={{
            Content: {
              message: 'title_dotNetCoresetEnvVariablesB',
              values: formatStyles.table({
                columns: getColumnsEnv(useIntl()),
                data: getEnvVarsData('')
              })
            }
          }}
        />
      </>
    )
  },
  {
    titleMsg: 'title_runContrast',
    Content: { message: 'detail_dotnetCoreRunContrast' }
  },
  ...commonFinalSteps
]

const stepsLinuxContainerNormalizeAgentDownload = [
  {
    ...stepSelectContainerMethod,
    branchKey: 'dotnet-core-container-method',
    branches: {
      docker: stepsDockerContainerNormalizeAgentDownload,
      kubernetes: [
        {
          ...stepSelectKubernetesOperator,
          branchKey: 'dotnet-core-kubernetes-op',
          branches: {
            helm: [
              stepKubernetesDownloadSetup,
              stepKubernetesLabelDeployments,
              stepKubernetesInstallDeployHelm,
              ...commonFinalSteps
            ],
            manifest: [
              stepContainerInstallOperator,
              stepContainerConfigureOperator,
              stepContainerInjectWorkloads,
              stepContainerClusterAgentAndAgent,
              ...commonFinalSteps
            ]
          }
        }
      ]
    }
  }
]

const stepsWindowsContainerNormalizeAgentDownload = [
  {
    ...stepSelectDockerMethod,
    branchKey: 'dotnet-container-method'
  },
  {
    titleMsg: 'title_buildImageWithAgent',
    defaultIsOpen: true,
    Content: () => {
      const [tab, setTab] = React.useState('x64')

      return (
        <>
          <StepContent
            step={{
              Content: {
                message: 'detail_addBuildImageWithAgent'
              }
            }}
          />
          <TabList
            className={css`
              margin-bottom: 16px;
            `}
            value={tab}
            onChange={(_, value) => setTab(value)}
          >
            <Tab label="x64" value="x64" />
          </TabList>
          <CodeBlock
            style={{ width: '100%', maxHeight: 'none' }}
            message={`From mcr.microsoft.com/dotnet/aspnet:6.0

# Hidden for brevity...

COPY --from=contrast/agent-dotnet-core:latest /contrast /contrast

# Required variables to load the agent.
ENV CORECLR_PROFILER_PATH 64=/contrast/runtimes/linux-${tab}/native/ContrastProfiler.so \\
      CORECLR_ENABLE_PROFILING=1
      CORECLR_PROFILER={8B2CE134-0948-48CA-A4B2-80DDAD9F5791}`}
          />
          <StepContent
            step={{
              Content: {
                message: 'detail_addBuildImage',
                values: {
                  code: 'docker build -t my-app-with-contrast .'
                }
              }
            }}
          />
        </>
      )
    }
  },
  stepAgentConfig,
  stepContainerAddContrastConfigurationToImage,
  stepContainerRunYourContainer,
  ...commonFinalSteps
]

const stepsWindowsInstallerNormalizeAgentDownload = [
  {
    titleMsg: 'title_downloadInstaller',
    defaultIsOpen: true,
    Content: {
      message: 'detail_dotnetCoreDownloadInstaller',
      values: {
        code: `Invoke-WebRequest https://www.nuget.org/api/v2/package/Contrast.CoreIIS.Installer/ -OutFile Contrast.CoreIIS.Installer.zip
Expand-Archive .\\Contrast.CoreIIS.Installer.zip -DestinationPath .\\Contrast.CoreIIS.Installer
`
      }
    }
  },
  stepAgentConfig,
  stepContainerRunInstaller,
  ...commonFinalSteps
]

const stepsWindowsBasicNormalizeAgentDownload = [
  {
    titleMsg: 'title_installAgent',
    defaultIsOpen: true,
    Content: {
      message: 'detail_downloadAndExtractAgent',
      values: {
        os: 'windows',
        code: `Invoke-WebRequest https://www.nuget.org/api/v2/package/Contrast.SensorsNetCore/ -OutFile Contrast.SensorsNetCore.zip
Expand-Archive .\\Contrast.SensorsNetCore.zip -DestinationPath .\\Contrast.SensorsNetCore
Move-Item .\\Contrast.SensorsNetCore\\contentFiles\\any\\netstandard2.0\\contrast .\\`
      }
    }
  },
  stepAgentConfig,
  {
    titleMsg: 'title_setEnvVariables',
    Content: () => (
      <StepContent
        step={{
          Content: {
            message: 'detail_dotnetCoreSetEnvVars',
            values: formatStyles.table({
              columns: getColumnsEnv(useIntl()),
              data: getEnvVarsData('windows')
            })
          }
        }}
      />
    )
  },
  {
    titleMsg: 'title_runContrast',
    Content: { message: 'detail_dotnetCoreRunContrast' }
  },
  ...commonFinalSteps
]

const stepsWindowsAzureNormalizeAgentDownload = [
  {
    defaultIsOpen: true,
    titleMsg: 'title_selectAnApplication',
    Content: {
      message: 'detail_dotnetCoreSelectAnApplication',
      values: formatStyles.link({
        isExternal: true,
        href: 'https://learn.microsoft.com/en-us/azure/app-service/quickstart-dotnetcore?tabs=netframework48&pivots=development-environment-vs'
      })
    }
  },
  stepConfigureAppSettingsAzureNormalize,
  {
    titleMsg: 'title_addExtensionForApplication',
    Content: {
      message: 'detail_dotnetCoreAzureAddExtensionForApplication',
      values: formatStyles.link({
        isExternal: true,
        href: 'https://portal.azure.com/#home'
      })
    }
  },
  ...commonFinalSteps
]

const stepSelectPipelineMethod = {
  defaultIsOpen: true,
  titleMsg: 'title_selectDeployMethodPipeline',
  Content: {
    options: [
      {
        value: 'github',
        Content: 'Github',
        icon: <IconGithub color="black" />
      },
      {
        value: 'bitbucket',
        Content: 'Bitbucket',
        icon: <IconBitbucket color="blue50"></IconBitbucket>
      }
    ]
  }
}

const githubWindowsInstallAgent = {
  defaultIsOpen: true,
  titleMsg: 'title_installAgent',
  Content: {
    message: 'detail_installGithubDotnetCoreWindows',
    values: {
      code1: `- name: Install Agent
  shell: bash
  run: |
    - Invoke-WebRequest https://www.nuget.org/api/v2/package/Contrast.SensorsNetCore/ -OutFile Contrast.SensorsNetCore.zip
    - Expand-Archive .\\Contrast.SensorsNetCore.zip -DestinationPath .\\Contrast.SensorsNetCore
    - Move-Item .\\Contrast.SensorsNetCore\\contentFiles\\any\\netstandard2.0\\contrast .\\
`,
      code2: `env:
  CORECLR_PROFILER_PATH_64: <DirectoryRoot>\\contrast\\runtimes\\win-x64\\native\\ContrastProfiler.dll
  CORECLR_PROFILER_PATH_32: <DirectoryRoot>\\contrast\\runtimes\\win-x86\\native\\ContrastProfiler.dll
  CORECLR_PROFILER: {8B2CE134-0948-48CA-A4B2-80DDAD9F5791}
  CORECLR_ENABLE_PROFILING: 1
  CONTRAST__API__TOKEN: \${{ secrets.ContrastAPIToken }}
  CONTRAST__APPLICATION__NAME: \${{ secrets.ContrastApplicationName }}
  CONTRAST__APPLICATION__METADATA: \${{ secrets.ContrastApplicationMetadata }}
`
    }
  }
}

const githubLinuxInstallAgent = {
  defaultIsOpen: true,
  titleMsg: 'title_installAgent',
  Content: {
    message: 'detail_installGithubDotnetCoreLinux',
    values: {
      code1: `- name: Install Agent
  shell: bash
  run: |
    curl https://www.nuget.org/api/v2/package/Contrast.SensorsNetCore/ --output Contrast.SensorsNetCore.nupkg
    unzip Contrast.SensorsNetCore.nupkg -d Contrast.SensorsNetCore
    mv -r Contrast.SensorsNetCore/contentFiles/any/netstandard2.0/contrast ./
`,
      code2: `env:
  CORECLR_PROFILER_PATH_64: <DirectoryRoot>/contrast/runtimes/linux-x64/native/ContrastProfiler.so
  CORECLR_PROFILER: {8B2CE134-0948-48CA-A4B2-80DDAD9F5791}
  CORECLR_ENABLE_PROFILING: 1
  CONTRAST__API__TOKEN: \${{ secrets.ContrastAPIToken }}
  CONTRAST__APPLICATION__NAME: \${{ secrets.ContrastApplicationName }}
  CONTRAST__APPLICATION__METADATA: \${{ secrets.ContrastApplicationMetadata }}
`,
      token: '{{ secrets.ContrastAPIToken }}',
      name: '{{ secrets.ContrastApplicationName }}',
      metadata: '{{ secrets.ContrastApplicationMetadata }}',
      directory: '<DirectoryRoot>',
      code: coreclrProfiler
    }
  }
}

const bitbucketWindowsInstallAgent = {
  defaultIsOpen: true,
  titleMsg: 'title_installAgent',
  Content: {
    message: 'detail_installBitbucketDotnetCoreWindows',
    values: {
      code1: `- step:
  script:
    - Invoke-WebRequest https://www.nuget.org/api/v2/package/Contrast.SensorsNetCore/ -OutFile Contrast.SensorsNetCore.zip
    - Expand-Archive .\\Contrast.SensorsNetCore.zip -DestinationPath .\\Contrast.SensorsNetCore
    - Move-Item .\\Contrast.SensorsNetCore\\contentFiles\\any\\netstandard2.0\\contrast .\\
`,
      code2: `- set CORECLR_PROFILER_PATH_64=<DirectoryRoot>\\contrast\\runtimes\\win-x64\\native\\ContrastProfiler.dll
- set CORECLR_PROFILER_PATH_32=<DirectoryRoot>\\contrast\\runtimes\\win-x86\\native\\ContrastProfiler.dll
- set CORECLR_ENABLE_PROFILING=1
- set CORECLR_PROFILER={8B2CE134-0948-48CA-A4B2-80DDAD9F5791}
`
    }
  }
}

const bitbucketLinuxInstallAgent = {
  defaultIsOpen: true,
  titleMsg: 'title_installAgent',
  Content: {
    message: 'detail_installBitbucketDotnetCoreLinux',
    values: {
      code1: `- step:
  script:
    - curl -L https://www.nuget.org/api/v2/package/Contrast.SensorsNetCore/ --output Contrast.SensorsNetCore.nupkg
    - unzip Contrast.SensorsNetCore.nupkg -d Contrast.SensorsNetCore
    - mv -r Contrast.SensorsNetCore/contentFiles/any/netstandard2.0/contrast ./
`,
      code2: `- export CORECLR_PROFILER_PATH_64=/contrast/runtimes/linux-64/native/ContrastProfiler.so
- export CORECLR_ENABLE_PROFILING=1
- export CORECLR_PROFILER={8B2CE134-0948-48CA-A4B2-80DDAD9F5791}
`
    }
  }
}

const windowsPipelineSteps = [
  {
    ...stepSelectPipelineMethod,
    branchKey: 'core-windows-pipeline-method',
    branches: {
      github: [
        githubWindowsInstallAgent,
        stepAgentConfigPipeline,
        ...commonFinalSteps
      ],
      bitbucket: [
        bitbucketWindowsInstallAgent,
        stepAgentConfigPipeline,
        ...commonFinalSteps
      ]
    }
  }
]

const linuxPipelineSteps = [
  {
    ...stepSelectPipelineMethod,
    branchKey: 'core-linux-pipeline-method',
    branches: {
      github: [
        githubLinuxInstallAgent,
        stepAgentConfigPipeline,
        ...commonFinalSteps
      ],
      bitbucket: [
        bitbucketLinuxInstallAgent,
        stepAgentConfigPipeline,
        ...commonFinalSteps
      ]
    }
  }
]

export const dotNetCoreWizardStepsNormalizeAgentDownload = [
  {
    ...stepSelectOperatingSystemLegacyNoDetails,
    branches: {
      linux: [
        {
          ...selectMethodDotNetCoreNormalizeAgentDownload,
          branchKey: 'dotnet-core-method-linux',
          branches: {
            basic: stepsLinuxBasicNormalizeAgentDownload,
            container: stepsLinuxContainerNormalizeAgentDownload,
            pipeline: linuxPipelineSteps
          }
        }
      ],
      windows: [
        {
          ...selectWindowsMethodDotNetCoreNormalizeAgentDownload,
          branchKey: 'dotnet-core-method-windows',
          branches: {
            installer: stepsWindowsInstallerNormalizeAgentDownload,
            basic: stepsWindowsBasicNormalizeAgentDownload,
            container: stepsWindowsContainerNormalizeAgentDownload,
            azure: stepsWindowsAzureNormalizeAgentDownload,
            pipeline: windowsPipelineSteps
          }
        }
      ]
    }
  }
]
